// src/utils/plural.js

/**
 * Uniwersalna funkcja do obsługi polskiej liczby mnogiej.
 * Zasada:
 * - 1       -> forma 'one' (np. zadanie)
 * - 2, 3, 4 -> forma 'few' (np. zadania) - z wyjątkiem 12, 13, 14
 * - 0, 5+   -> forma 'many' (np. zadań)
 *
 * @param {number} count - Liczba, na podstawie której odmieniamy.
 * @param {string} one - Forma dla liczby 1.
 * @param {string} few - Forma dla liczb 2, 3, 4 (końcówki).
 * @param {string} many - Forma dla liczb 0, 5 i więcej.
 * @returns {string} Poprawnie odmienione słowo.
 */
const pluralize = (count, one, few, many) => {
  const absCount = Math.abs(count);

  if (absCount === 1) {
    return one;
  }

  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;

  // Dla liczb kończących się na 2, 3, 4, ale nie 12, 13, 14
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return few;
  }

  return many;
};

/**
 * Zwraca poprawną formę słowa "zadanie".
 * @param {number} count - Liczba zadań.
 * @returns {'zadanie' | 'zadania' | 'zadań'}
 */
export const pluralizeTasks = (count) => pluralize(count, 'zadanie', 'zadania', 'zadań');

/**
 * Zwraca poprawną formę słowa "dzień".
 * Słowo "dzień" ma prostszą odmianę: 1 dzień, 2+ dni.
 * @param {number} count - Liczba dni.
 * @returns {'dzień' | 'dni'}
 */
export const pluralizeDays = (count) => (Math.abs(count) === 1 ? 'dzień' : 'dni');

/**
 * Zwraca poprawną formę słowa "zamrożenie".
 * @param {number} count - Liczba zamrożeń.
 * @returns {'zamrożenie' | 'zamrożenia' | 'zamrożeń'}
 */
export const pluralizeFreezes = (count) => pluralize(count, 'zamrożenie', 'zamrożenia', 'zamrożeń');

// Możesz tutaj przenieść również inne funkcje pomocnicze związane z tekstem,
// które znajdowały się na dole Dashboard.js, aby wszystko było w jednym miejscu.

/**
 * Zwraca zdanie o postępie w zadaniach, np. "5 zadań z 7 zadań".
 * @param {number} solved - Liczba rozwiązanych zadań.
 * @param {number} total  - Łączna liczba zadań.
 * @returns {string}
 */
export function tasksProgressPhrase(solved, total) {
  const solvedWord = pluralizeTasks(solved);
  // Po przyimku "z" stoi dopełniacz: 1 -> "zadania", reszta -> "zadań"
  const totalWord = total === 1 ? 'zadania' : 'zadań';
  return `${solved} ${solvedWord} z ${total} ${totalWord}`;
}

/**
 * Zwraca komunikat motywacyjny na podstawie liczby rozwiązanych zadań.
 * @param {number} count - Liczba rozwiązanych zadań.
 * @returns {string}
 */
export function solvedDashboardTasksMessage(count) {
    const tiers = [
        {
          max: 0,
          text:
            'Witamy w Matematly! Wybierz interesujące Cię kategorie, ' +
            'poziom trudności i zacznij rozwiązywać zadania.' +
            'Powodzenia!'
        },
        {
          max: 30,
          text: `Udało Ci się już rozwiązać ${count} ${pluralizeTasks(
            count
          )} – to dobry początek, ale nie możesz spocząć na laurach!`
        },
        {
          max: 100,
          text: `Świetnie Ci idzie! Już ${count} ${pluralizeTasks(
            count
          )} za Tobą; do okrągłej setki zostało niewiele. Pamiętaj, że w statystykach ` +
            'możesz zobaczyć, ile zadań rozwiązałeś w każdej kategorii!'
        },
        {
          max: Infinity,
          text: `Twoje ${count} ${pluralizeTasks(
            count
          )} na koncie to nie w kij dmuchał – niewielu osiągnęło taki wynik. ` +
            'Powodzenia w dalszej nauce!'
        }
      ];
    
      const tier = tiers.find(t => count <= t.max);
      return tier ? tier.text : '';
}